import { FileText } from "lucide-react";

import CopyToClipboard from "@components/ui-components/copy-to-clipboard";
import LoadingButton from "@components/ui-components/loading-button";
import StatusChip from "@components/ui-components/status-chip";
import { formatCurrency } from "@helpers/utils";
import { formatDate } from "@helpers/date";
import {
    FREQUENCY_LABEL,
    KYC_LABEL,
    KYC_TONE,
    PAYMENT_MODEL_LABEL,
    PAYOUT_LABEL,
    PAYOUT_TONE,
    PLATFORM_LABEL
} from "@constants/panel/common";
import { RECORD_STATUS_LABEL, RECORD_STATUS_TONE, statusKey } from "./constants";

// `copyValue` splits what is shown from what is copied, so a shortened api_key
// still puts the whole key on the clipboard.
const MonoId = ({ value, id, copyValue }) => {
    if (!value) return <span className="text-muted">-</span>;
    return (
        <span className="inline-flex items-center gap-1.5">
            <code className="rounded bg-light px-1.5 py-0.5 text-xs text-gray-700">{value}</code>
            <CopyToClipboard text={copyValue || value} id={id} width={13} />
        </span>
    );
};

// The list is read-only — it exists so the next step can pick a brand and read
// its api_key, not to edit anything.
export const getBrandColumns = () => [
    {
        Header  : "Brand",
        accessor: "name",
        sortable: true,
        Cell    : ({ value }) => <div className="font-medium text-gray-900">{value || "-"}</div>
    },
    {
        Header  : "External id",
        accessor: "ext_id",
        Cell    : ({ value, row }) => <MonoId value={value} id={`ext-${row._id}`} />
    },
    {
        Header  : "Status",
        accessor: "status",
        sortable: true,
        Cell    : ({ value }) => (
            <StatusChip
                tone={RECORD_STATUS_TONE[value] || "neutral"}
                label={RECORD_STATUS_LABEL[value] || value || "-"} />
        )
    },
    {
        Header  : "Payment model",
        accessor: "payment_model",
        Cell    : ({ value }) => <span className="text-gray-700">{PAYMENT_MODEL_LABEL[value] || value || "-"}</span>
    },
    {
        Header  : "Debit note",
        accessor: "debit_note_frequency",
        Cell    : ({ value }) => <span className="text-gray-700">{FREQUENCY_LABEL[value] || value || "-"}</span>
    },
    {
        Header  : "Platform",
        accessor: "platform_name",
        Cell    : ({ value }) => <span className="text-gray-700">{PLATFORM_LABEL[value] || value || "-"}</span>
    },
    {
        Header  : "KYC window",
        accessor: "kyc_window",
        Cell    : ({ value }) => <span className="text-gray-700">{value ? `${value} days` : "-"}</span>
    },
    {
        // Copyable in full: it is what step 3 sends as the holder's api_key.
        Header  : "API key",
        accessor: "api_key",
        Cell    : ({ value, row }) => (value
            ? <MonoId value={`${value.slice(0, 10)}…`} copyValue={value} id={`key-${row._id}`} />
            : <span className="text-muted">-</span>)
    },
    {
        Header  : "Created",
        accessor: "createdAt",
        sortable: true,
        Cell    : ({ value }) => <span className="text-gray-700">{formatDate(value)}</span>
    }
];

// The list is already scoped to one brand by the path, so no brand column —
// every row belongs to the brand in the picker above it. The columns are the
// fields this endpoint actually returns, nothing speculative.
export const getAffiliateColumns = ({ onCreateInvoice }) => [
    {
        Header  : "Publisher",
        accessor: "holder_name",
        sortable: true,
        Cell    : ({ value, row }) => (
            <div>
                <div className="font-medium text-gray-900">{value || "-"}</div>
                <div className="text-xs text-muted">{row.email || "-"}</div>
            </div>
        )
    },
    {
        // The row's own `_id` is what the invoice carries as `holder_id` —
        // shown so a wrong row is visible before the invoice is raised, not
        // after. (The row also has a `holder_id` field; the invoice does not
        // use it.)
        Header  : "_id",
        accessor: "_id",
        Cell    : ({ value }) => <MonoId value={value} id={`affiliate-${value}`} />
    },
    {
        Header  : "KYC",
        accessor: "kyc_status",
        Cell    : ({ value }) => (
            <StatusChip tone={KYC_TONE[statusKey(value)] || "neutral"} label={KYC_LABEL[statusKey(value)] || value || "-"} />
        )
    },
    {
        Header  : "Payout",
        accessor: "payout_status",
        Cell    : ({ value }) => (
            <StatusChip tone={PAYOUT_TONE[statusKey(value)] || "neutral"} label={PAYOUT_LABEL[statusKey(value)] || value || "-"} />
        )
    },
    {
        Header  : "Pending",
        accessor: "pending_amount",
        sortable: true,
        Cell    : ({ value }) => <span className="text-gray-700">{formatCurrency(value)}</span>
    },
    {
        // Null until this publisher has been invoiced, which is the whole point
        // of the column: it says who still needs one.
        Header  : "Invoice",
        accessor: "invoice_number",
        Cell    : ({ value }) => (value
            ? <span className="text-gray-700">{value}</span>
            : <span className="text-muted">Not invoiced</span>)
    },
    {
        Header  : "Added",
        accessor: "createdAt",
        sortable: true,
        Cell    : ({ value }) => <span className="text-gray-700">{formatDate(value)}</span>
    },
    {
        Header  : "",
        accessor: "action",
        Cell    : ({ row }) => (
            <div className="flex justify-end">
                <LoadingButton
                    size="xs"
                    icon={FileText}
                    text="Create invoice"
                    onClick={() => onCreateInvoice(row)} />
            </div>
        )
    }
];
